/**
 * KBDeleteDocumentModal.tsx — `D-40`, the confirmation before a deletion that
 * cannot be undone.
 *
 * Omar decided on 2026-09-06 that deleting a source PDF also deletes every
 * summary written from it, *"including colleagues' edits, additions and any
 * comments"*, and gave the wording and the two buttons verbatim. He also asked
 * that the affected summaries be *"clear before confirmation, including any that
 * also reference other PDFs"* — so the list is not decoration, it is the part of
 * this dialog that carries the decision.
 *
 * Everything shown comes from `GET /v1/documents/{id}/deletion-preview`, which
 * the server computes with the same function that performs the deletion. This
 * component decides nothing about what is affected; it renders the answer.
 *
 * While that answer is still loading, or if it fails, the confirm button is
 * disabled. A dialog that offers an irreversible action beside an empty list
 * says "nothing will be lost" to the person reading it, and that is exactly the
 * sentence it must not say by accident.
 */
import { useEffect, useRef } from "react";

import { I } from "../../components/icons";
import { apiErrorMessage } from "../../lib/api";
import type { KbDeletionPreviewDto } from "../../lib/kbApi";

interface KBDeleteDocumentModalProps {
  filename: string;
  preview: KbDeletionPreviewDto | undefined;
  isLoading: boolean;
  error: Error | null;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function KBDeleteDocumentModal({
  filename,
  preview,
  isLoading,
  error,
  isDeleting,
  onCancel,
  onConfirm,
}: KBDeleteDocumentModalProps): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus lands on «Annulla», never on the destructive button: a stray Return
  // pressed at the moment the dialog appears must not delete anything.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !isDeleting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, isDeleting]);

  const pages = preview?.pages ?? [];
  const shared = pages.filter((p) => p.also_from.length > 0);
  const edited = pages.filter((p) => p.has_manual_contributions);
  // `preview !== undefined` is the clause doing the work today, and the other
  // two are deliberate rather than decorative: `useKbDeletionPreview` sets
  // `gcTime: 0`, so a failed reopen has no cached answer to fall back on and
  // `data` is undefined whenever `error` is set. The moment somebody gives that
  // query a cache — a reasonable thing to want — react-query keeps the previous
  // `data` through a failed refetch, and this dialog would arm its confirm
  // beside a list describing the world as it was. Measured, and recorded here
  // rather than trimmed, so the next person removing `gcTime: 0` sees what it
  // is holding up.
  const ready = preview !== undefined && !isLoading && error === null;

  return (
    <div
      onClick={() => {
        if (!isDeleting) onCancel();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,22,28,0.45)",
        zIndex: 70,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kb-del-title"
        style={{
          width: 620,
          maxWidth: "100%",
          maxHeight: "85vh",
          background: "var(--c-bg-elev)",
          borderRadius: 12,
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <I.Alert size={16} style={{ color: "var(--c-err)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="kb-del-title" style={{ fontSize: 13.5, fontWeight: 600 }}>
              Elimina «{filename}»
            </div>
          </div>
        </div>

        <div style={{ padding: "16px 18px", overflowY: "auto" }}>
          {/* Omar's wording, verbatim. It is the sentence he approved, so it is
              not paraphrased, re-ordered or softened here. */}
          <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
            Eliminando questo PDF verranno eliminate anche tutte le sintesi collegate, comprese le
            modifiche, le aggiunte e gli eventuali commenti dei colleghi. Vuoi procedere?
          </p>

          {/* `D-42`, Omar 2026-09-06. Three statements he asked the confirmation
              to carry, beyond the sentence above.

              (1) WHICH of the two deletions this is. The product has two, and a
              person who has just been told that deleting a PDF destroys every
              summary needs to know that this is that one and not the other. The
              single-summary control says the mirror of this sentence.
              (2) That it leaves BOTH screens. Under `D-42` the project copy and
              the Knowledge Base copy are one source, so a person deleting from
              either place must not expect the file to survive in the other.
              (3) What happens to saved reports — text kept, references dropped.
              Worded as what a reader will SEE, not as what the database does. */}
          <div
            data-testid="kb-delete-scope"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--c-bg-sunk)",
              display: "grid",
              gap: 6,
            }}
          >
            <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
              Stai eliminando <strong>il PDF di origine e tutte le sue sintesi</strong>, non una
              singola sintesi.
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
              Il PDF sparirà da <strong>entrambe le schermate</strong>: «Documenti del progetto» e
              Knowledge Base.
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
              Le <strong>analisi salvate restano</strong>, con il loro testo. Spariranno soltanto i
              collegamenti e i riferimenti alle sintesi eliminate.
            </p>
          </div>

          {isLoading && (
            <p style={{ fontSize: 12.5, color: "var(--c-ink-3)", marginTop: 14 }}>
              Verifica delle sintesi collegate in corso…
            </p>
          )}

          {error !== null && (
            <p style={{ fontSize: 12.5, color: "var(--c-err)", marginTop: 14 }}>
              Non è stato possibile verificare le sintesi collegate: {apiErrorMessage(error)}.
              Riprova prima di eliminare.
            </p>
          )}

          {ready && pages.length === 0 && (
            <p style={{ fontSize: 12.5, color: "var(--c-ink-3)", marginTop: 14 }}>
              Nessuna sintesi collegata a questo PDF.
            </p>
          )}

          {ready && pages.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--c-ink-2)",
                  marginTop: 16,
                  marginBottom: 6,
                }}
              >
                {pages.length === 1
                  ? "1 sintesi verrà eliminata"
                  : `${pages.length} sintesi verranno eliminate`}
              </div>
              <ul
                data-testid="kb-delete-affected-pages"
                style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}
              >
                {pages.map((p) => (
                  <li key={p.id} style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 500 }}>{p.title}</span>
                    {p.has_manual_contributions && (
                      <span style={{ color: "var(--c-warn)", marginLeft: 6 }}>
                        · contiene modifiche o aggiunte dei colleghi
                      </span>
                    )}
                    {p.also_from.length > 0 && (
                      <span style={{ color: "var(--c-ink-3)", marginLeft: 6 }}>
                        · basata anche su {p.also_from.join(", ")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {shared.length > 0 && (
                // Spelled out as well as marked per row: this is the
                // consequence a person is least likely to expect, and a marker
                // at the end of a line is easy to read past.
                <p style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 12 }}>
                  {shared.length === 1
                    ? "1 di queste sintesi è basata anche su altri PDF: verrà eliminata comunque, ma gli altri PDF restano nella Knowledge Base."
                    : `${shared.length} di queste sintesi sono basate anche su altri PDF: verranno eliminate comunque, ma gli altri PDF restano nella Knowledge Base.`}
                </p>
              )}
              {edited.length > 0 && (
                <p style={{ fontSize: 12.5, color: "var(--c-warn)", marginTop: 8 }}>
                  {edited.length === 1
                    ? "1 di queste sintesi contiene modifiche o aggiunte fatte da una persona. Verranno eliminate anch'esse."
                    : `${edited.length} di queste sintesi contengono modifiche o aggiunte fatte da una persona. Verranno eliminate anch'esse.`}
                </p>
              )}
            </>
          )}

          {ready && preview.pending_reviews > 0 && (
            <p style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 12 }}>
              {preview.pending_reviews === 1
                ? "1 contraddizione aperta riguarda questo PDF: sparirà dalla coda di revisione insieme al documento."
                : `${preview.pending_reviews} contraddizioni aperte riguardano questo PDF: spariranno dalla coda di revisione insieme al documento.`}
            </p>
          )}

          {ready && preview.decided_reviews > 0 && (
            // CORRECTED after an independent review. This used to read «restano
            // registrate nel registro attività», which was false in the way that
            // matters: the copy kept in `audit_log` is served by NO endpoint, and
            // the only register a person can actually open — the «Storico» tab of
            // the review queue — reads `review_requests`, which is exactly what
            // the cascade destroys. The sentence reassured someone about a record
            // they would then be unable to find.
            <p style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 8 }}>
              {preview.decided_reviews === 1
                ? "1 decisione già presa su una contraddizione sparirà dallo Storico delle revisioni. Ne resta traccia solo nel registro interno, non consultabile da questa schermata."
                : `${preview.decided_reviews} decisioni già prese su contraddizioni spariranno dallo Storico delle revisioni. Ne resta traccia solo nel registro interno, non consultabile da questa schermata.`}
            </p>
          )}

          {ready && preview.stranded_recoverable > 0 && (
            // SPLIT after an independent review. One sentence promised recovery
            // for every stranded document; the server refuses «Controlla di
            // nuovo» for ever on any document a person has already ruled on, so
            // for those the promise was false. The two counts come from the
            // server, computed with the same predicate the button itself uses.
            //
            // «un amministratore» rather than «premi». The reason has CHANGED and
            // the sentence has not: it was written because this dialog was open to
            // every member while the recovery control was admin-only. Since `D-41`
            // (Omar, 2026-09-06) only an administrator can open this dialog at all,
            // so the reader IS one — but the recovery may be taken by a different
            // person on a different day, and naming the role rather than the reader
            // stays true either way. Kept deliberately, not left unexamined.
            <p style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 12 }}>
              {preview.stranded_recoverable === 1
                ? "1 altro documento era in attesa di una decisione su una contraddizione con questo PDF. Resterà non disponibile finché un amministratore non premerà «Controlla di nuovo»."
                : `${preview.stranded_recoverable} altri documenti erano in attesa di una decisione su una contraddizione con questo PDF. Resteranno non disponibili finché un amministratore non premerà «Controlla di nuovo».`}
            </p>
          )}

          {ready && preview.stranded_blocked > 0 && (
            <p style={{ fontSize: 12.5, color: "var(--c-err)", marginTop: 8 }}>
              {/* No cause asserted, and the remedy named is one that exists.
                  The first version said «una persona ha già deciso» — only one
                  of several reasons the server can refuse — and «servirà una
                  decisione di un amministratore», for which there is no screen,
                  no queue and no endpoint. Re-uploading is what actually works. */}
              {preview.stranded_blocked === 1
                ? "1 altro documento resterà non disponibile e NON potrà essere riaperto automaticamente. Per rimetterlo in circolazione occorrerà caricarlo di nuovo."
                : `${preview.stranded_blocked} altri documenti resteranno non disponibili e NON potranno essere riaperti automaticamente. Per rimetterli in circolazione occorrerà caricarli di nuovo.`}
            </p>
          )}

          {ready && preview.run_in_flight && (
            <p style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 8 }}>
              Questo PDF è in elaborazione in questo momento. L&apos;elaborazione verrà interrotta e
              non lascerà nulla nella Knowledge Base.
            </p>
          )}
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid var(--c-line)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            ref={cancelRef}
            className="btn btn-ghost btn-sm"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Annulla
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={onConfirm}
            disabled={!ready || isDeleting}
          >
            {isDeleting ? "Eliminazione…" : "Elimina PDF e sintesi"}
          </button>
        </div>
      </div>
    </div>
  );
}
